import { QueryCache, QueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import { stringify } from 'qs';
import React, { useEffect } from 'react';
import { Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ADMINISTRATION_ROUTE_PATHS } from '@/administration/administration.constants';
import { agentBuilderAvailable } from '@/agentBuilder/agentBuilder.utilities';
import { Builder } from '@/builder/Builder.molecule';
import { Root } from '@/core/Root';
import { sqLicenseManagementStore } from '@/core/core.stores';
import { isProtractor } from '@/core/utilities';
import { knowledgeManagementEnabled } from '@/knowledgeManagement/knowledgeManagement.availability';
import { LicenseManagementStoreProvider } from '@/licenseManagement/licenseManagement.store';
import { HeadlessCaptureStandby } from '@/main/HeadlessCaptureStandby';
import { LoadError } from '@/main/LoadError.page';
import { LocalisationWrapper } from '@/main/LocalisationWrapper';
import { Main } from '@/main/Main.page';
import { ProtectedComponent } from '@/main/ProtectedComponents';
import { RouteError } from '@/main/RouteError.atom';
import { Unauthorized } from '@/main/Unauthorized.page';
import { Login } from '@/main/auth/Login.page';
import { ResetPassword } from '@/main/auth/ResetPassword.page';
import { Unsubscribe } from '@/notifications/Unsubscribe.page';
import { setupInterceptors } from '@/requests/axios.utilities';
import { canReadAuditTrail, canReadVersionLogs, isTrustedUser } from '@/services/authorization.service';
import { agentSkillsEnabled } from '@/services/systemConfiguration.utilities';
import { isHttpAbortError } from '@/utilities/http.utilities';
import { logError } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { initializeReduxDevtools } from '@/utilities/reduxDevtools.utilities';
import { initializeHeadlessCaptureMode } from '@/utilities/screenshot.utilities';
import { WorkbenchWrapperPage } from '@/workbench/WorkbenchWrapper.page';
import { APPSERVER_API_PREFIX, ApiProvider, createAxiosInstance, setupApiSdk } from '@seeqdev/api';
import { QTip } from '@seeqdev/qomponents';

// React.lazy only works via default exports so we import shim files which export a default component
// matching the given route
const Administration = React.lazy(() => import('@/defaultExportShims/Administration'));
const WorksheetWrapper = React.lazy(() => import('@/defaultExportShims/WorksheetWrapper'));
const HomeScreen = React.lazy(() => import('@/defaultExportShims/HomeScreen'));
const HomeScreenAddOnHost = React.lazy(() => import('@/defaultExportShims/HomeScreenAddOnHost'));
const ViewWrapper = React.lazy(() => import('@/defaultExportShims/ViewWrapper'));
const License = React.lazy(() => import('@/defaultExportShims/License'));
const LogTracker = React.lazy(() => import('@/defaultExportShims/LogTracker'));
const DataLabWrapper = React.lazy(() => import('@/defaultExportShims/DataLabWrapper'));
const AuditTrail = React.lazy(() => import('@/defaultExportShims/AuditTrail'));
const ReportTemplateThumbnail = React.lazy(() => import('@/defaultExportShims/ReportTemplateThumbnail'));
const NotificationsHistory = React.lazy(() => import('@/defaultExportShims/NotificationsHistory'));
const NotificationsManagement = React.lazy(() => import('@/defaultExportShims/NotificationsManagement'));
const VantageManagement = React.lazy(() => import('@/defaultExportShims/VantageManagement'));
const DataLabManagement = React.lazy(() => import('@/defaultExportShims/DataLabManagement'));
const ExternalMLManagement = React.lazy(() => import('@/defaultExportShims/ExternalMLManagement'));

const AiAssistant = React.lazy(() => import('@/defaultExportShims/AiAssistant'));
const Usage = React.lazy(() => import('@/defaultExportShims/Usage'));
const ValueCapture = React.lazy(() => import('@/defaultExportShims/ValueCapture'));
const VersionLog = React.lazy(() => import('@/defaultExportShims/VersionLog'));
const MarkdownPreview = React.lazy(() => import('@/defaultExportShims/MarkdownPreview'));
const WorkspaceMarkdownCapture = React.lazy(() => import('@/defaultExportShims/WorkspaceMarkdownCapture'));
const AgentBuilder = React.lazy(() => import('@/defaultExportShims/AgentBuilder'));
const AgentBuilderDashboard = React.lazy(() => import('@/defaultExportShims/AgentBuilderDashboard'));
const SkillEditor = React.lazy(() => import('@/defaultExportShims/SkillEditor'));
const SkillsDashboard = React.lazy(() => import('@/defaultExportShims/SkillsDashboard'));
const KnowledgeManagement = React.lazy(() => import('@/defaultExportShims/KnowledgeManagement'));
const TreeBuilder = React.lazy(() => import('@/defaultExportShims/TreeBuilder'));
const TreeBuilderOverview = React.lazy(() => import('@/defaultExportShims/TreeBuilderOverview'));

initializeHeadlessCaptureMode();
prepareForTests();
initializeReduxDevtools();

const apiQueryCache = new QueryCache();
const apiQueryClient = new QueryClient({ queryCache: apiQueryCache });

const axiosInstance = createAxiosInstance();

setupInterceptors(axiosInstance);
setupApiSdk({
  baseUrl: APPSERVER_API_PREFIX,
  axiosInstance,
  serializeParams: (params) => stringify(params, { indices: false }),
});

function prepareForTests() {
  if (isProtractor()) {
    const originalDebounce = _.debounce;
    // @ts-ignore For protractor tests we want to disable debounce completely
    _.debounce = (fn: any, wait: any, { isAllowedDuringTest, ...options } = { isAllowedDuringTest: false }) => {
      return isAllowedDuringTest
        ? originalDebounce(fn, wait, options)
        : _.assign(fn, { cancel: _.noop, flush: _.noop });
    };
  }
}

const worksheetRoutePaths = [
  'workbook/:workbookId/worksheet/:worksheetId',
  'workbook/:workbookId/worksheet',
  'workbook/:workbookId',
  ':currentFolderId/workbook/:workbookId/worksheet/:worksheetId',
  ':currentFolderId/workbook/:workbookId/worksheet',
  ':currentFolderId/workbook/:workbookId',
  'view/worksheet/:workbookId/:worksheetId',
  'view/worksheet/:workbookId',
  ':currentFolderId/view/worksheet/:workbookId/:worksheetId',
  ':currentFolderId/view/worksheet/:workbookId',
  'present/worksheet/:workbookId/:worksheetId',
  'present/worksheet/:workbookId',
];

/**
 * Application is the de-facto Root component of the application where all the routes are configured
 */
export const Application = () => {
  useEffect(() => {
    window.onerror = function (errorMsg, url, lineNumber, colNumber, errObject) {
      if (errObject && errObject.stack) {
        logError(errObject);
      } else {
        logError(new Error(`${errorMsg} in ${url}, line: ${lineNumber}, column: ${colNumber}`));
      }
    };
    const rejectionHandle = (event: PromiseRejectionEvent) => {
      if (!isHttpAbortError(event.reason)) {
        logError(formatMessage`Unhandled exception: ${event.reason}`);
      }
      event.preventDefault();
    };
    window.addEventListener('unhandledrejection', rejectionHandle);
    return () => {
      window.removeEventListener('unhandledrejection', rejectionHandle);
    };
  }, []);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path="/" element={<Main />} errorElement={<RouteError />}>
          <Route path="/" element={<WorkbenchWrapperPage />}>
            <Route index element={<ProtectedComponent component={<HomeScreen />} />} />

            <Route path="ai" element={<ProtectedComponent component={<AiAssistant />} />} />
            <Route path="md/*" element={<ProtectedComponent component={<MarkdownPreview />} />} />
            <Route
              path="present/workspace-markdown/:chatId"
              element={<ProtectedComponent component={<WorkspaceMarkdownCapture />} />}
            />
            <Route
              path="present/workspace-markdown-renderer/:chatId"
              element={<ProtectedComponent component={<WorkspaceMarkdownCapture />} />}
            />
            <Route
              path="agent-builder"
              element={
                <ProtectedComponent
                  permissions={agentBuilderAvailable}
                  component={
                    <React.Suspense>
                      <AgentBuilderDashboard />
                    </React.Suspense>
                  }
                />
              }
            />
            <Route
              path="agent-builder/new"
              element={
                <ProtectedComponent
                  permissions={agentBuilderAvailable}
                  component={
                    <React.Suspense>
                      <AgentBuilder />
                    </React.Suspense>
                  }
                />
              }
            />
            <Route
              path="agent-builder/:agentBuilderId"
              element={
                <ProtectedComponent
                  permissions={agentBuilderAvailable}
                  component={
                    <React.Suspense>
                      <AgentBuilder />
                    </React.Suspense>
                  }
                />
              }
            />
            {/* Skills Builder - integrated under Agent Builder, gated by AgentSkills feature flag */}
            <Route
              path="agent-builder/skills"
              element={
                <ProtectedComponent
                  permissions={() => agentBuilderAvailable() && agentSkillsEnabled()}
                  component={
                    <React.Suspense>
                      <SkillsDashboard />
                    </React.Suspense>
                  }
                />
              }
            />
            <Route
              path="agent-builder/skills/new"
              element={
                <ProtectedComponent
                  permissions={() => agentBuilderAvailable() && agentSkillsEnabled()}
                  component={
                    <React.Suspense>
                      <SkillEditor />
                    </React.Suspense>
                  }
                />
              }
            />
            <Route
              path="agent-builder/skills/:skillId"
              element={
                <ProtectedComponent
                  permissions={() => agentBuilderAvailable() && agentSkillsEnabled()}
                  component={
                    <React.Suspense>
                      <SkillEditor />
                    </React.Suspense>
                  }
                />
              }
            />

            <Route
              path="knowledge-management"
              element={
                <ProtectedComponent
                  permissions={knowledgeManagementEnabled}
                  component={
                    <React.Suspense>
                      <KnowledgeManagement />
                    </React.Suspense>
                  }
                />
              }
            />

            <Route path="workbooks" element={<ProtectedComponent component={<HomeScreen />} />} />

            <Route path=":currentFolderId/folder" element={<ProtectedComponent component={<HomeScreen />} />} />

            {_.map(worksheetRoutePaths, (path) => (
              <Route key={path} path={path} element={<ProtectedComponent component={<WorksheetWrapper />} />} />
            ))}

            <Route
              path="hsa"
              element={<ProtectedComponent component={<HomeScreenAddOnHost />} permissions={isTrustedUser} />}
            />

            <Route path="view/:viewId" element={<ProtectedComponent component={<ViewWrapper />} />} />

            <Route path="tree-builder">
              <Route index element={<ProtectedComponent component={<TreeBuilderOverview />} />} />
              <Route path=":tableId" element={<ProtectedComponent component={<TreeBuilder />} />} />
            </Route>

            <Route path="administration" element={<ProtectedComponent component={<Administration />} />}>
              {ADMINISTRATION_ROUTE_PATHS.map((path) => (
                <Route key={path} path={path} />
              ))}
            </Route>

            <Route
              path="license"
              element={<ProtectedComponent component={<License />} permissions={isTrustedUser} />}
            />

            <Route
              path="logs"
              element={<ProtectedComponent component={<LogTracker />} permissions={isTrustedUser} />}
            />

            <Route path=":folderId/project/:projectId" element={<DataLabWrapper />} />
            <Route path="project/:projectId" element={<DataLabWrapper />} />

            <Route
              path="/impact"
              element={<ProtectedComponent component={<ValueCapture />} permissions={isTrustedUser} />}
            />

            <Route
              path="auditTrail"
              element={<ProtectedComponent component={<AuditTrail />} permissions={canReadAuditTrail} />}
            />

            <Route
              path="version-log"
              element={
                <ProtectedComponent
                  component={
                    <React.Suspense>
                      <VersionLog />
                    </React.Suspense>
                  }
                  permissions={canReadVersionLogs}
                />
              }
            />

            <Route
              path="notifications-management"
              element={
                <React.Suspense>
                  <NotificationsManagement />
                </React.Suspense>
              }
            />

            <Route
              path="vantage-management"
              element={
                <React.Suspense>
                  <VantageManagement />
                </React.Suspense>
              }
            />

            <Route
              path="external-ml-management"
              element={
                <React.Suspense>
                  <ExternalMLManagement />
                </React.Suspense>
              }
            />

            <Route
              path="manage-data-lab"
              element={
                <React.Suspense>
                  <DataLabManagement />
                </React.Suspense>
              }
            />

            <Route
              path="my-usage"
              element={
                <React.Suspense>
                  <Usage />
                </React.Suspense>
              }
            />

            <Route
              path="notifications-history"
              element={
                <React.Suspense>
                  <NotificationsHistory />
                </React.Suspense>
              }
            />
          </Route>
          <Route path="/" element={<LocalisationWrapper />}>
            <Route path="/headless-capture-standby" element={<HeadlessCaptureStandby />} />

            <Route
              path="report-template/:templateId"
              element={<ProtectedComponent component={<ReportTemplateThumbnail />} />}
            />

            <Route path="login" element={<Login />} />

            <Route path="reset-password" element={<ResetPassword />} />

            <Route path="workbook/builder" element={<ProtectedComponent component={<Builder />} />} />

            <Route path="unauthorized" element={<Unauthorized />} />

            <Route path="forbidden" element={<Unauthorized />} />

            <Route
              path="load-error"
              element={
                <LoadError
                  header="LOAD_ERROR.SERVER_HEADER"
                  message1="LOAD_ERROR.SERVER_MESSAGE1"
                  message2="LOAD_ERROR.SERVER_MESSAGE2"
                  retryInterval={3000}
                />
              }
            />

            <Route
              path="no-worksheet"
              element={
                <LoadError
                  header="LOAD_ERROR.NO_WORKSHEET_HEADER"
                  message1="LOAD_ERROR.NO_WORKSHEET_MESSAGE1"
                  message2="LOAD_ERROR.RETURN_TO_WORKBENCH"
                  retryInterval={0}
                />
              }
            />

            <Route
              path="no-workbook"
              element={
                <LoadError
                  header="LOAD_ERROR.NO_WORKBOOK_HEADER"
                  message1="LOAD_ERROR.NO_WORKBOOK_MESSAGE1"
                  message2="LOAD_ERROR.RETURN_TO_WORKBENCH"
                  retryInterval={0}
                />
              }
            />

            <Route path="unsubscribe/:notificationId" element={<Unsubscribe />} />

            <Route
              path="no-websocket"
              element={
                <LoadError
                  header="LOAD_ERROR.WEBSOCKET_HEADER"
                  message1="LOAD_ERROR.WEBSOCKET_MESSAGE1"
                  message2="LOAD_ERROR.WEBSOCKET_MESSAGE2"
                  retryInterval={0}
                />
              }
            />

            <Route
              path="connection-error"
              element={
                <LoadError
                  header="LOAD_ERROR.SERVER_HEADER"
                  message1="LOAD_ERROR.SERVER_MESSAGE1"
                  message2="LOAD_ERROR.SERVER_MESSAGE2"
                  retryInterval={3000}
                />
              }
            />
          </Route>
        </Route>
      </>,
    ),
  );

  return (
    <Root>
      <LicenseManagementStoreProvider store={sqLicenseManagementStore}>
        <QTip />
        <ToastContainer theme="colored" icon={false} />
        <ApiProvider axiosInstance={axiosInstance} queryClient={apiQueryClient}>
          <RouterProvider router={router} />
        </ApiProvider>
      </LicenseManagementStoreProvider>
    </Root>
  );
};
